import clsx from 'clsx';
import dayjs from 'dayjs';
import { FC } from 'react';
import LocationIcon from '../../../assets/icons/location.svg?react';
import { Button } from '../../../components/Button';
import { Card, CardHeader } from '../../../components/Card';
import { Location } from '../../../components/Location';
import { TripTypeIcon } from '../../../components/TripTypeIcon';
import {
  openEditNote,
  openEditTripType,
  toggleTripMap,
  useTripData,
} from '../../../services/history';
import { UUID } from '../../../types';
import { formatDuration } from '../../../utils/formatDuration';
import { DataList } from '../../../components/DataList';
import { formatMileage } from '../../../utils/formatMileage';
import styles from './Trip.module.css';
import { blueCircle, dottedCircle } from './views';
import { FormattedMessage } from 'react-intl';

interface TripProps {
  id: UUID;
}

export const Trip: FC<TripProps> = ({ id }) => {
  const trip = useTripData(id);

  if (!trip) {
    return null;
  }

  const {
    routeWithIgnitionOn,
    routeWithIgnitionOff,
    drivingDuration,
    stopDuration,
    distance,
    objectName,
    flatTrip,
  } = trip;

  return (
    <Card>
      <CardHeader className={styles.header}>
        <div>
          {dayjs(routeWithIgnitionOn.firstCoordinate.dateTime).format('L')}
        </div>
        <div className={styles.total}>
          <span className={clsx('light', styles.slim)}>
            <FormattedMessage id="Duration.Total" />:
          </span>{' '}
          {formatDuration(drivingDuration + stopDuration)}
        </div>
      </CardHeader>
      <div className={styles.trip}>
        <div className={styles.tripPart}>
          <div className={styles.sml}>
            <div>
              {dayjs(routeWithIgnitionOn.firstCoordinate.dateTime).format(
                'HH:mm'
              )}
            </div>
            <FormattedMessage tagName="div" id="Date.Start" />
          </div>
          <div className={styles.line}>{blueCircle}</div>
          <div>
            <DataList>
              <FormattedMessage tagName="dt" id="DrivingDuration" />
              <dd>{formatDuration(drivingDuration)}</dd>

              <FormattedMessage tagName="dt" id="Mileage" />
              <dd>{formatMileage(distance)}</dd>

              <FormattedMessage tagName="dt" id="Vehicle" />
              <dd>{objectName}</dd>

              <FormattedMessage tagName="dt" id="Trip.Type" />
              <Button
                variant="text"
                className={styles.alignedBtn}
                compact
                onClick={() => openEditTripType(flatTrip.id)}
              >
                <TripTypeIcon
                  className={styles.tripTypeIcn}
                  tripType={flatTrip.tripTypeOverride.tripType}
                />
                <FormattedMessage
                  tagName="div"
                  id={`Trip.Type.${flatTrip.tripTypeOverride.tripType}`}
                />
              </Button>
            </DataList>
          </div>
        </div>
        {routeWithIgnitionOff && (
          <div className={styles.tripPart}>
            <div className={styles.sml}>
              <div>
                {dayjs(routeWithIgnitionOff.firstCoordinate.dateTime).format(
                  'HH:mm'
                )}
              </div>
              <FormattedMessage tagName="div" id="Route.Stop" />
            </div>
            <div className={clsx(styles.line, styles.endLine)}>
              {dottedCircle}
            </div>
            <div className={styles.stopPart}>
              <DataList>
                <FormattedMessage tagName="dt" id="StopDuration" />
                <dd>{formatDuration(stopDuration)}</dd>
              </DataList>
              <div className={styles.sml}>
                <div className={styles.slim}>
                  <FormattedMessage id="Location" />
                </div>
                <div>
                  <Location {...routeWithIgnitionOff.firstCoordinate} />
                </div>
                <Button
                  variant="outlined"
                  compact
                  onClick={() => toggleTripMap(id)}
                >
                  <FormattedMessage tagName="div" id="ShowOnMap" />
                  <LocationIcon className={styles.locationIcn} />
                </Button>
              </div>
              <div className={styles.notesContainer}>
                <div>
                  <div className={clsx(styles.sml, styles.slim)}>Notes</div>
                  <div className={styles.notes}>{flatTrip.note ?? '—'}</div>
                </div>
                <Button
                  className={styles.alignedBtn}
                  variant="text"
                  compact
                  onClick={() => openEditNote(flatTrip.id)}
                >
                  <FormattedMessage id="Edit" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
